import React, { useEffect, useRef, useState } from 'react'
import {
  View,
  FlatList,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Keyboard,
  Platform,
} from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Screen, Text, Icon, Header, HiddenText, FeatureTooltip, type IconName } from '@components'
import { useLanguage } from '@context'
import { useChatbot, type ChatMessage } from './hook'
import { getStyle } from './style'
import { useScreen, useKeyboardOffset } from '@hooks'

export const Chatbot = () => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { isWeb } = useScreen()
  const keyboardOffset = useKeyboardOffset()
  const { t } = useLanguage()
  const flatListRef = useRef<FlatList>(null)
  const containerRef = useRef<View | null>(null)
  const inputBarRef = useRef<View | null>(null)
  const [webPaddingBottom, setWebPaddingBottom] = useState(0)
  const [nativeKeyboardPadding, setNativeKeyboardPadding] = useState(0)
  const [height, setHeight] = useState(40)

  useEffect(() => {
    if (!isWeb) return
    if (typeof window === 'undefined' || !window.visualViewport) return

    const vv = window.visualViewport
    const update = () => {
      const node = containerRef.current as unknown as HTMLElement | null
      if (!node) return
      const containerBottom = node.getBoundingClientRect().bottom
      const viewportBottom = vv.height + vv.offsetTop
      setWebPaddingBottom(Math.max(0, containerBottom - viewportBottom))
    }

    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [isWeb])

  // iOS only: the window does NOT resize when the keyboard opens, so we
  // lift the input bar ourselves. We measure its real on-screen position
  // and pad by exactly its overlap with the keyboard — this stays correct
  // regardless of the bottom tab bar / safe-area, unlike a fixed offset.
  //
  // Android is intentionally NOT handled here. `windowSoftInputMode=adjustResize`
  // (AndroidManifest) shrinks the window when the keyboard opens, and
  // MainActivity dropped immersive-sticky so that resize actually happens;
  // the flex layout then lifts the input above the keyboard on its own.
  // Adding JS padding on top double-counts (the original gap), and RN's
  // keyboard-frame events are unreliable under adjustResize (the window is
  // resized rather than overlaid), which caused intermittent mis-measures
  // — the input hiding behind the keyboard. We rely on the OS instead,
  // matching `KeyboardAwareView`, a passthrough on Android.
  // Web is handled by the visualViewport effect above.
  useEffect(() => {
    if (Platform.OS !== 'ios') return

    const onShow = Keyboard.addListener('keyboardWillShow', (e) => {
      const keyboardTop = e.endCoordinates.screenY
      // `nativeKeyboardPadding` is always 0 here (reset on hide), so the
      // measured bottom is the input bar's un-padded resting position.
      inputBarRef.current?.measureInWindow((_x, y, _width, measuredHeight) => {
        const overlap = Math.max(0, y + measuredHeight - keyboardTop)
        setNativeKeyboardPadding(overlap)
      })
    })
    const onHide = Keyboard.addListener('keyboardWillHide', () => {
      setNativeKeyboardPadding(0)
    })

    return () => {
      onShow.remove()
      onHide.remove()
    }
  }, [])

  const {
    messages,
    inputText,
    setInputText,
    isThinking,
    pendingActions,
    sendMessage,
    confirmAction,
    cancelAction,
    startNewConversation,
  } = useChatbot()

  const handleSend = () => {
    sendMessage()
    setTimeout(() => {
      flatListRef.current?.scrollToEnd({ animated: true })
    }, 100)
  }

  // Starter prompts shown on the empty assistant screen. Tapping one sends it
  // straight away; they disappear once the conversation has any message.
  const suggestions: { icon: IconName; key: string }[] = [
    { icon: 'note', key: 'chatbot.suggestions.notes' },
    { icon: 'messageCircle', key: 'chatbot.suggestions.conversations' },
    { icon: 'work', key: 'chatbot.suggestions.sector' },
    { icon: 'calendar', key: 'chatbot.suggestions.calendar' },
    { icon: 'gift', key: 'chatbot.suggestions.birthdays' },
    { icon: 'search', key: 'chatbot.suggestions.findContact' },
  ]

  const handleSuggestionPress = (text: string) => {
    sendMessage(text)
    setTimeout(() => {
      flatListRef.current?.scrollToEnd({ animated: true })
    }, 100)
  }

  const renderMessage = ({ item }: { item: ChatMessage }) => {
    if (item.role === 'user') {
      return (
        <View style={styles.userBubble}>
          <Text type="body" style={styles.userBubbleText}>
            {item.content}
          </Text>
        </View>
      )
    }

    const showConfirmCard =
      item.pendingActions && item.pendingActions.length > 0 && pendingActions.length > 0

    return (
      <View>
        <View style={styles.aiBubble}>
          <Text type="body" style={styles.aiBubbleText}>
            {item.content}
          </Text>
        </View>
        {showConfirmCard && (
          <View style={styles.confirmCard}>
            {item.pendingActions!.map((action, idx) => (
              <Text key={idx} type="body" style={styles.confirmDescription}>
                {action.description}
              </Text>
            ))}
            <View style={styles.confirmButtons}>
              <TouchableOpacity
                style={styles.confirmButton}
                onPress={confirmAction}
                activeOpacity={0.7}
              >
                <Text type="bodyBold" style={styles.confirmButtonText}>
                  {t('chatbot.confirm')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={cancelAction}
                activeOpacity={0.7}
              >
                <Text type="bodyBold" style={styles.cancelButtonText}>
                  {t('chatbot.cancel')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
    )
  }

  const isKeyboardOpen = keyboardOffset > 0

  const renderEmpty = () =>
    isKeyboardOpen ? null : (
      <View style={styles.welcomeContainer}>
        <Icon name="star" size={40} color={theme.colors.primary} />
        <Text type="body" style={styles.welcomeText}>
          {t('chatbot.welcome')}
        </Text>
        <Text type="bodySmall" style={styles.suggestionsTitle}>
          {t('chatbot.suggestionsTitle')}
        </Text>
        <View style={styles.suggestionsWrap}>
          {suggestions.map((s) => (
            <TouchableOpacity
              key={s.key}
              style={styles.suggestionChip}
              activeOpacity={0.7}
              onPress={() => handleSuggestionPress(t(s.key))}
            >
              <Icon name={s.icon} size={theme.iconSize.small} color={theme.colors.primary} />
              <Text type="bodySmall" style={styles.suggestionChipText}>
                {t(s.key)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    )

  return (
    <Screen noPaddingBottom>
      <View
        ref={containerRef}
        style={[
          styles.container,
          isWeb && { paddingBottom: webPaddingBottom },
          !isWeb && { paddingBottom: nativeKeyboardPadding },
        ]}
      >
        <Header title={t('chatbot.title')} iconRight="plus" onPressRight={startNewConversation} />
        <FeatureTooltip
          id="chatbot_intro"
          title={t('featureTooltip.chatbot_intro.title')}
          description={t('featureTooltip.chatbot_intro.description')}
          icon="star"
        />
        <FlatList
          ref={flatListRef}
          data={messages}
          renderItem={renderMessage}
          keyExtractor={(item) => item.id}
          style={styles.messageList}
          contentContainerStyle={[
            styles.messageListContent,
            messages.length === 0 && { flex: 1, justifyContent: 'center' },
          ]}
          ListEmptyComponent={renderEmpty}
          onContentSizeChange={() => {
            if (messages.length > 0) {
              flatListRef.current?.scrollToEnd({ animated: true })
            }
          }}
        />
        {isThinking && (
          <View style={styles.typingContainer}>
            <ActivityIndicator size="small" color={theme.colors.primary} />
            <Text type="bodySmall" style={styles.typingText}>
              {t('chatbot.thinking')}
            </Text>
          </View>
        )}
        <HiddenText text={inputText} setHeight={setHeight} />
        <View ref={inputBarRef} style={styles.inputBar}>
          <TextInput
            style={[styles.textInput, isWeb && { height }]}
            value={inputText}
            onChangeText={setInputText}
            placeholder={t('chatbot.placeholder')}
            placeholderTextColor={theme.colors.grey50}
            multiline
            onSubmitEditing={handleSend}
            editable={!isThinking}
          />
          <TouchableOpacity
            style={[
              styles.sendButton,
              {
                backgroundColor:
                  inputText.trim() && !isThinking ? theme.colors.primary : theme.colors.grey30,
              },
            ]}
            onPress={handleSend}
            disabled={!inputText.trim() || isThinking}
            activeOpacity={0.7}
          >
            <Icon name="send" size={20} color={theme.colors.white} />
          </TouchableOpacity>
        </View>
      </View>
    </Screen>
  )
}
